import { EmployeeDashboardStats, Employee, DepartmentStats, CountryStats } from '../../types/employee-directory';
import { employeeDirectoryService } from './employeeDirectoryService';

// Cache configuration
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const EMPLOYEE_METRICS_CACHE_KEY = 'employeeDirectoryMetricsCache';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Load cache from localStorage
const loadCache = <T>(key: string): CacheEntry<T> | null => {
  const cached = localStorage.getItem(key);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      console.error('Error parsing employee directory metrics cache:', e);
      return null;
    }
  }
  return null;
};

// Save cache to localStorage
const saveCache = <T>(key: string, data: T) => {
  try {
    const cacheData: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (e) {
    console.error('Error saving employee directory metrics cache:', e);
  }
};

// Clear cache
export const clearEmployeeDirectoryMetricsCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(EMPLOYEE_METRICS_CACHE_KEY)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.error('Error clearing employee directory metrics cache:', e);
  }
};

/**
 * Employee Directory Metrics Cache Service
 * Provides cached access to employee directory metrics for the JLS Apps dashboard
 */
export const employeeDirectoryMetricsCache = {
  /**
   * Get Employee Directory App Hub Metrics
   * Returns metrics formatted for the JLS Apps dashboard
   */
  async getEmployeeDirectoryAppHubMetrics(): Promise<{
    totalEmployees: number;
    totalDepartments: number;
    totalCountries: number;
    activeEmployees: number;
    lastActivity: string;
  }> {
    const cacheKey = `${EMPLOYEE_METRICS_CACHE_KEY}_app_hub`;
    const cached = loadCache<{
      totalEmployees: number;
      totalDepartments: number;
      totalCountries: number;
      activeEmployees: number;
      lastActivity: string;
    }>(cacheKey);
    const now = Date.now();

    // Return cached data if fresh
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      // Get dashboard metrics from the service
      const metrics = await employeeDirectoryService.getDashboardMetrics();
      
      // Format for App Hub display
      const appHubMetrics = {
        totalEmployees: metrics.totalEmployees,
        totalDepartments: metrics.totalDepartments,
        totalCountries: metrics.totalCountries,
        activeEmployees: metrics.activeEmployees,
        lastActivity: metrics.lastUpdated ? new Date(metrics.lastUpdated).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric'
        }) : '-'
      };

      // Save to cache
      saveCache(cacheKey, appHubMetrics);
      return appHubMetrics;
    } catch (error) {
      console.error('Error getting Employee Directory App Hub metrics:', error);
      
      // Return cached data if available, even if stale
      if (cached) {
        console.warn('Using stale cache for Employee Directory App Hub metrics');
        return cached.data;
      }
      
      return {
        totalEmployees: 0,
        totalDepartments: 0,
        totalCountries: 0,
        activeEmployees: 0,
        lastActivity: '-'
      };
    }
  },

  /**
   * Get full dashboard metrics with caching
   */
  async getDashboardMetrics(): Promise<EmployeeDashboardStats> {
    const cacheKey = `${EMPLOYEE_METRICS_CACHE_KEY}_dashboard`;
    const cached = loadCache<EmployeeDashboardStats>(cacheKey);
    const now = Date.now();

    // Return cached data if fresh
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      const metrics = await employeeDirectoryService.getDashboardMetrics();
      
      // Save to cache
      saveCache(cacheKey, metrics);
      return metrics;
    } catch (error) {
      console.error('Error getting Employee Directory dashboard metrics:', error);
      
      // Return cached data if available
      if (cached) {
        console.warn('Using stale cache for Employee Directory dashboard metrics');
        return cached.data;
      }
      
      // Return default metrics
      return {
        totalEmployees: 0,
        totalDepartments: 0,
        totalCountries: 0,
        totalCities: 0,
        activeEmployees: 0,
        newHiresThisMonth: 0,
        upcomingBirthdays: [],
        upcomingAnniversaries: [],
        departmentBreakdown: [],
        countryBreakdown: [],
        lastUpdated: new Date().toISOString()
      };
    }
  },

  /**
   * Get department statistics with caching
   */
  async getDepartmentStats(): Promise<DepartmentStats[]> {
    const cacheKey = `${EMPLOYEE_METRICS_CACHE_KEY}_department_stats`;
    const cached = loadCache<DepartmentStats[]>(cacheKey);
    const now = Date.now();

    // Return cached data if fresh
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      const stats = await employeeDirectoryService.getDepartmentStats();
      
      // Save to cache
      saveCache(cacheKey, stats);
      return stats;
    } catch (error) {
      console.error('Error getting Employee Directory department stats:', error);
      
      // Return cached data if available
      if (cached) {
        console.warn('Using stale cache for Employee Directory department stats');
        return cached.data;
      }
      
      return [];
    }
  },

  /**
   * Get location/country statistics with caching
   */
  async getLocationStats(): Promise<CountryStats[]> {
    const cacheKey = `${EMPLOYEE_METRICS_CACHE_KEY}_location_stats`;
    const cached = loadCache<CountryStats[]>(cacheKey);
    const now = Date.now();

    // Return cached data if fresh
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      const stats = await employeeDirectoryService.getLocationStats();
      
      // Save to cache
      saveCache(cacheKey, stats);
      return stats;
    } catch (error) {
      console.error('Error getting Employee Directory location stats:', error);
      
      // Return cached data if available
      if (cached) {
        console.warn('Using stale cache for Employee Directory location stats');
        return cached.data;
      }
      
      return [];
    }
  },

  /**
   * Clear all employee directory metrics caches
   */
  clearAllCaches(): void {
    clearEmployeeDirectoryMetricsCache();
  },

  /**
   * Force refresh of all cached data
   */
  async forceRefresh(): Promise<void> {
    // Clear all caches
    this.clearAllCaches();
    
    // Pre-load fresh data
    await Promise.all([
      this.getEmployeeDirectoryAppHubMetrics(),
      this.getDashboardMetrics(),
      this.getDepartmentStats(),
      this.getLocationStats()
    ]);
  }
};